import React, { createContext, useContext, useRef, useState, useEffect, useCallback, useMemo } from "react";
import styles from "./FocusContextRegionProvider.module.css";
import { isNonEmptyObject } from "../../../utils";

const FocusContext = createContext();

export const useFocus = () => useContext(FocusContext);

export function FocusProvider({ children }) {
	// registered refs by id
	const registeredRefs = useRef(new Map());
	// focused ids = Set<string>
	const [focusedIds, setFocusedIds] = useState(new Set());
	// per-id highlight options: { id: { left, top, width, height } }
	const [highlightOptions, setHighlightOptions] = useState({});
	// Map<string, CSSStyleDeclarationSnapshot>
	const originalStyles = useRef(new Map());
	// --------------------------
	// REGISTER / UNREGISTER
	// --------------------------
	const register = useCallback((id, ref) => {
		if (!id || !ref.current) {
			return;
		}
		registeredRefs.current.set(id, ref);
	}, []);

	const unregister = useCallback((id) => {
		registeredRefs.current.delete(id);
	}, []);
	// --------------------------
	// FOCUS MULTIPLE ELEMENTS (WITH OPTIONS)
	// --------------------------
	/**
	 * Accepts a list of:
	 * - string IDs  → focus("card1", "card2")
	 * - objects { id, options } → focus({ id:"card1", options:{ width:"50%"} })
	 */
	const focus = useCallback((...items) => {
		const ids = new Set();
		const newOptions = {};
		for (const item of items) {
			// If provided only an ID
			if (typeof item === "string") {
				ids.add(item);
			}
			// If provided ID + options
			else if (typeof item === "object" && item.id) {
				ids.add(item.id);
				newOptions[item.id] = item.options ?? {};
			}
		}
		setFocusedIds(ids);
		setHighlightOptions((prev) => ({ ...prev, ...newOptions }));
	}, []);
	const unfocus = useCallback(() => {
		setFocusedIds(new Set());
		setHighlightOptions({});
	}, []);
	// --------------------------
	// ESC resets focus
	// --------------------------
	useEffect(() => {
		const onKey = (e) => {
			if (e.defaultPrevented) {
				return;
			}
			if (e.key === "Escape") {
				unfocus();
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [unfocus]);
	// --------------------------
	// APPLY STYLES / PARTIAL HIGHLIGHTING
	// --------------------------
	useEffect(() => {
		registeredRefs.current.forEach((ref, id) => {
			const el = ref.current;
			if (!el) {
				return;
			}
			const options = highlightOptions[id];
			if (focusedIds.has(id)) {
				// Save original style ONCE
				if (!originalStyles.current.has(id)) {
					originalStyles.current.set(id, {
						zIndex: ref.current.style.zIndex,
						position: ref.current.style.position,
						filter: ref.current.style.filter,
						pointerEvents: ref.current.style.pointerEvents,
						background: ref.current.style.background,
					});
				}
				el.style.transition = "all 0.3s ease";
				el.style.zIndex = 1002;
				el.style.position = "relative";
				el.style.filter = "none";
				el.style.pointerEvents = "auto";
				el.style.background = "var(--color-white)";
				if (isNonEmptyObject(options)) {
					// remove any existing dark overlay
					let overlay = el?.current?.querySelector(".dark-overlay");
					if (overlay) {
						overlay.remove();
					}
					if (!overlay) {
						overlay = document.createElement("div");
						overlay.className = "dark-overlay";
						overlay.style.position = "absolute";
						overlay.style.top = "0";
						overlay.style.left = "0";
						overlay.style.width = "100%";
						overlay.style.height = "100%";
						overlay.style.background = "rgba(0, 0, 0, 0.6)";
						overlay.style.zIndex = "1001";
						overlay.style.pointerEvents = "all";
						overlay.style.transition = "all 0.3s ease";
						ref.current.appendChild(overlay);
					}
					if (options?.left) {
						overlay.style.left = options.left;
					}
					if (options?.width) {
						overlay.style.width = options.width;
					}
					if (options?.top) {
						overlay.style.top = options.top;
					}
					if (options?.height) {
						overlay.style.height = options.height;
					}
				}
			} else {
				const original = originalStyles.current.get(id);
				if (original) {
					el.style.zIndex = original.zIndex;
					el.style.position = original.position;
					el.style.filter = original.filter;
					el.style.pointerEvents = original.pointerEvents;
					el.style.background = original.background;
				}
				const overlay = el.querySelector(".dark-overlay");
				if (overlay) {
					overlay.remove();
				}
				originalStyles.current.delete(id);
			}
		});
	}, [focusedIds, highlightOptions]);
	// --------------------------
	// MEMOIZED CONTEXT VALUE
	// --------------------------
	const value = useMemo(
		() => ({
			register,
			unregister,
			focus,
			unfocus,
			focusedIds,
			highlightOptions,
		}),
		[register, unregister, unfocus, focus, focusedIds, highlightOptions]
	);
	return (
		<FocusContext.Provider value={value}>
			{children}
			{focusedIds?.size > 0 && <div className={styles.overlay} />}
		</FocusContext.Provider>
	);
}
